import { useTranslation } from "@redotech/redo-i18n";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  Layout,
  RedoCheckboxCard,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox-card";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import type { ProductProgressMap } from "../hooks/use-product-progress";
import { getProductLabel } from "../product-labels";
import type { ProductEntry } from "../products";
import * as styles from "./landing-product-picker.module.css";
import {
  getLandingProductSectionLabel,
  getLandingProductSubtext,
  type LandingProductSection,
} from "./landing-products";

interface LandingProductPickerProps {
  enabledProducts: Record<string, boolean>;
  onSelect: (product: ProductEntry) => void;
  productProgress: ProductProgressMap | null;
  sections: LandingProductSection[];
  selectedProductId?: string;
}

export const LandingProductPicker = memo(function LandingProductPicker({
  enabledProducts,
  onSelect,
  productProgress,
  sections,
  selectedProductId,
}: LandingProductPickerProps) {
  const { t } = useTranslation();
  return (
    <Flex
      aria-label={t("Onboarding products")}
      className={styles.picker}
      dir="column"
      flex={1}
      gap="4xl"
      minH="none"
      overflowY="auto"
      pr="sm"
      role="radiogroup"
      w="full"
    >
      {sections.map((section) => (
        <Flex dir="column" gap="md" key={section.id} shrink={0}>
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {getLandingProductSectionLabel(section.id)}
          </Text>
          <Flex dir="column" gap="md">
            {section.products.map((product) => {
              const ProductIcon = product.icon;
              const isActive = enabledProducts[product.id] ?? false;
              const isSelected = selectedProductId === product.id;

              return (
                <RedoCheckboxCard
                  checked={isSelected}
                  dataTestId={`landing-product-${product.id}`}
                  icon={
                    ProductIcon ? (
                      <RedoIcon Icon={ProductIcon} size={20} />
                    ) : undefined
                  }
                  inputSize="sm"
                  inputType="radio"
                  key={product.id}
                  layout={Layout.LEFT}
                  onClick={() => onSelect(product)}
                  title={
                    <Flex align="center" gap="sm" minW="none">
                      <Text
                        fontSize="sm"
                        fontWeight={isSelected ? "medium" : "regular"}
                        textColor="primary"
                      >
                        {getProductLabel(product)}
                      </Text>
                      {isActive && (
                        <RedoBadge
                          color="success"
                          size="xs"
                          text={t("Active")}
                        />
                      )}
                    </Flex>
                  }
                >
                  <Text fontSize="xs" textColor="secondary">
                    {getLandingProductSubtext(product, {
                      isActive,
                      progress: productProgress?.[product.id],
                    })}
                  </Text>
                </RedoCheckboxCard>
              );
            })}
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
});
